import { useEffect, useState, useMemo } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Mail, Building2, Phone, Sparkles, Copy, Check, RotateCcw, Search, KeyRound, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";

const STATUS_OPTS = [
  { value: "new", label: "未対応", tone: "bg-amber-100 text-amber-800 border-amber-200" },
  { value: "contacted", label: "連絡済", tone: "bg-sky-100 text-sky-800 border-sky-200" },
  { value: "trial", label: "トライアル中", tone: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  { value: "closed", label: "終了", tone: "bg-slate-100 text-slate-600 border-slate-200" },
];
const statusOf = (v) => STATUS_OPTS.find((s) => s.value === v) || STATUS_OPTS[0];

function StatusBadge({ value }) {
  const s = statusOf(value);
  return <span className={`text-[11px] px-2 py-0.5 rounded-full border font-bold ${s.tone}`}>{s.label}</span>;
}

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return `${d.getMonth() + 1}/${d.getDate()} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

export default function LeadsManagement() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [issueResult, setIssueResult] = useState(null); // {login_id, password, ...}
  const [detail, setDetail] = useState(null); // lead detail dialog
  const [busy, setBusy] = useState(null); // lead.id while busy

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/leads");
      setLeads(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    return leads.filter((l) => {
      if (statusFilter !== "all" && l.status !== statusFilter) return false;
      if (!q.trim()) return true;
      const s = q.toLowerCase();
      return (
        (l.company || "").toLowerCase().includes(s) ||
        (l.name || "").toLowerCase().includes(s) ||
        (l.email || "").toLowerCase().includes(s)
      );
    });
  }, [leads, q, statusFilter]);

  const counts = useMemo(() => {
    const c = { all: leads.length };
    for (const s of STATUS_OPTS) c[s.value] = leads.filter((l) => l.status === s.value).length;
    return c;
  }, [leads]);

  // メアド毎の出現回数を計算 (重複検出用)
  const emailCount = useMemo(() => {
    const map = {};
    for (const l of leads) {
      const e = (l.email || "").toLowerCase().trim();
      if (e) map[e] = (map[e] || 0) + 1;
    }
    return map;
  }, [leads]);

  const issueAccount = async (lead) => {
    setBusy(lead.id);
    try {
      const { data } = await api.post(`/leads/${lead.id}/issue-account`);
      setIssueResult({ ...data, lead });
      if (!data.already_issued) toast.success("ログインIDを発行しました");
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(null);
    }
  };

  const resetPassword = async (lead) => {
    setBusy(lead.id);
    try {
      const { data } = await api.post(`/leads/${lead.id}/reset-password`);
      setIssueResult({ ...data, lead, reset: true });
      toast.success("パスワードを再発行しました");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(null);
    }
  };

  const setStatus = async (lead, newStatus) => {
    try {
      await api.put(`/leads/${lead.id}?status=${newStatus}`);
      setLeads((prev) => prev.map((l) => l.id === lead.id ? { ...l, status: newStatus } : l));
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="space-y-4" data-testid="leads-page">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900">トライアル申込み一覧</h1>
        <p className="text-sm text-slate-500 mt-1">LPからのお申込みは自動でアカウント発行・メール送信されます。下記は履歴の確認と、万が一の手動再発行用です。</p>
      </div>

      {/* ステータスフィルタ + 検索 */}
      <Card className="p-4 bg-white border border-slate-200">
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <FilterPill label={`全て (${counts.all})`} active={statusFilter === "all"} onClick={() => setStatusFilter("all")} testId="filter-all" />
          {STATUS_OPTS.map((s) => (
            <FilterPill key={s.value} label={`${s.label} (${counts[s.value] || 0})`} active={statusFilter === s.value} onClick={() => setStatusFilter(s.value)} testId={`filter-${s.value}`} tone={s.tone} />
          ))}
        </div>
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
          <Input
            value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="会社名・お名前・メールで検索"
            className="pl-9"
            data-testid="leads-search"
          />
        </div>
      </Card>

      {/* テーブル */}
      <Card className="bg-white border border-slate-200 overflow-hidden">
        {loading ? (
          <p className="py-12 text-center text-sm text-slate-400">読み込み中…</p>
        ) : filtered.length === 0 ? (
          <p className="py-12 text-center text-sm text-slate-400">該当するお申込みはありません</p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {filtered.map((l) => (
              <LeadRow
                key={l.id}
                lead={l}
                dupCount={emailCount[(l.email || "").toLowerCase().trim()] || 1}
                busy={busy === l.id}
                onIssue={() => issueAccount(l)}
                onReset={() => resetPassword(l)}
                onStatus={(s) => setStatus(l, s)}
                onShowDetail={() => setDetail(l)}
              />
            ))}
          </ul>
        )}
      </Card>

      <IssueResultDialog data={issueResult} onClose={() => setIssueResult(null)} />
      <LeadDetailDialog lead={detail} onClose={() => setDetail(null)} />
    </div>
  );
}

function FilterPill({ label, active, onClick, testId }) {
  return (
    <button onClick={onClick} data-testid={testId}
      className={`text-xs font-bold px-3 py-1.5 rounded-full border transition-colors ${
        active ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
      }`}>
      {label}
    </button>
  );
}

function LeadRow({ lead, dupCount = 1, busy, onIssue, onReset, onStatus, onShowDetail }) {
  const [expanded, setExpanded] = useState(false);
  const issued = !!lead.issued_user_id;
  const isDup = dupCount > 1;
  return (
    <li className="px-4 py-3" data-testid={`lead-row-${lead.id}`}>
      <div className="flex flex-wrap items-center gap-2">
        <button onClick={() => setExpanded(!expanded)} className="md:hidden p-1" aria-label="詳細">
          {expanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
        </button>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <button onClick={onShowDetail} className="font-bold text-slate-900 hover:text-blue-700 hover:underline text-sm" data-testid={`lead-name-${lead.id}`}>
              {lead.company}
            </button>
            <span className="text-xs text-slate-500">/ {lead.name}</span>
            <StatusBadge value={lead.status} />
            {isDup && (
              <span className="text-[10px] font-bold text-orange-800 bg-orange-100 border border-orange-300 px-2 py-0.5 rounded inline-flex items-center gap-0.5" data-testid={`dup-flag-${lead.id}`} title={`同じメールアドレスで${dupCount}件の申請があります`}>
                ⚠️ 重複 {dupCount}件
              </span>
            )}
            {lead.industry && <span className="text-[10px] text-slate-500 bg-slate-100 px-2 py-0.5 rounded">{lead.industry}</span>}
            {lead.store_count && <span className="text-[10px] text-slate-500 bg-slate-100 px-2 py-0.5 rounded">{lead.store_count}</span>}
            {lead.source && lead.source !== "lp" && (
              <span className="text-[10px] font-bold text-purple-800 bg-purple-50 border border-purple-200 px-2 py-0.5 rounded" data-testid={`source-badge-${lead.id}`} title={`流入元: ${lead.source}`}>
                📣 {lead.source}
              </span>
            )}
            {lead.coupon_applied && (
              <span className="text-[10px] font-bold text-amber-800 bg-gradient-to-r from-amber-100 to-pink-100 border border-amber-300 px-2 py-0.5 rounded" title={lead.coupon_label || ""} data-testid={`coupon-badge-${lead.id}`}>
                🎟️ {lead.coupon_code}
              </span>
            )}
          </div>
          <div className={`mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-slate-500 ${expanded ? "" : "hidden md:flex"}`}>
            <span className="inline-flex items-center gap-1"><Mail className="w-3 h-3" />{lead.email}</span>
            {lead.phone && <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" />{lead.phone}</span>}
            <span className="text-slate-400">受付: {fmtDate(lead.created_at)}</span>
            {issued && <span className="text-emerald-700 font-bold">✓ ID発行済: {lead.issued_login_id}</span>}
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <Select value={lead.status} onValueChange={onStatus}>
            <SelectTrigger className="h-8 text-xs w-32" data-testid={`status-${lead.id}`}><SelectValue /></SelectTrigger>
            <SelectContent>
              {STATUS_OPTS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
          {issued ? (
            <Button
              size="sm" variant="outline"
              onClick={onReset} disabled={busy}
              className="h-8 text-xs border-amber-300 text-amber-800 hover:bg-amber-50"
              data-testid={`reset-${lead.id}`}
            >
              <RotateCcw className="w-3.5 h-3.5 mr-1" />PW再発行
            </Button>
          ) : (
            <Button
              size="sm"
              onClick={onIssue} disabled={busy}
              className="h-8 text-xs bg-emerald-600 hover:bg-emerald-700"
              data-testid={`issue-${lead.id}`}
            >
              <Sparkles className="w-3.5 h-3.5 mr-1" />ログインID発行
            </Button>
          )}
        </div>
      </div>
    </li>
  );
}

// ===== 発行結果ダイアログ =====
function IssueResultDialog({ data, onClose }) {
  const [copied, setCopied] = useState({});

  if (!data) return null;
  const { login_id, password, lead, already_issued, reset } = data;

  const loginUrl = `${window.location.origin}/login`;

  const emailTemplate = password
    ? `${lead.name} 様

この度は「やさまるシフト」へのご関心、誠にありがとうございます。
無料トライアル(2ヶ月)用のアカウントを発行いたしましたので、下記よりログインください。

━━━━━━━━━━━━━━━━━━
ログインURL: ${loginUrl}
ログインID: ${login_id}
初期パスワード: ${password}
━━━━━━━━━━━━━━━━━━

セキュリティのため、ログイン後にパスワードの変更をおすすめします。
ご不明な点がございましたら、お気軽にこのメールにご返信ください。

やさまるシフト カスタマーサポート`
    : `${lead.name} 様

ログインIDのご案内です。

ログインURL: ${loginUrl}
ログインID: ${login_id}

(パスワードは既にお送り済みです。お忘れの場合は再発行いたします。)

やさまるシフト カスタマーサポート`;

  const copy = (key, value) => {
    navigator.clipboard.writeText(value).then(() => {
      setCopied({ ...copied, [key]: true });
      toast.success("コピーしました");
      setTimeout(() => setCopied((c) => ({ ...c, [key]: false })), 1500);
    });
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-xl" data-testid="issue-result-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <KeyRound className="w-5 h-5 text-emerald-600" />
            {reset ? "パスワード再発行" : already_issued ? "発行済みアカウント" : "ログインID 発行完了"}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-xs text-emerald-800">
            <p><b>{lead.company}</b> / {lead.name} 様<br />宛先: <b>{lead.email}</b></p>
            {already_issued && !reset && (
              <p className="mt-2 text-amber-700">※ このリードからは既にログインIDが発行済みです。パスワードを忘れた場合は「PW再発行」ボタンをお使いください。</p>
            )}
          </div>

          {/* ログイン情報カード */}
          <div className="border border-slate-200 rounded-lg overflow-hidden">
            <CredRow label="ログインURL" value={loginUrl} onCopy={() => copy("url", loginUrl)} copied={copied.url} />
            <CredRow label="ログインID" value={login_id} onCopy={() => copy("id", login_id)} copied={copied.id} bold />
            {password ? (
              <CredRow label="初期パスワード" value={password} onCopy={() => copy("pw", password)} copied={copied.pw} bold mono />
            ) : (
              <CredRow label="パスワード" value="(再表示不可。再発行が必要)" disabled />
            )}
          </div>

          {/* メール文面 */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-xs font-bold text-slate-700">📧 メール文面（テンプレ）</label>
              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => copy("mail", emailTemplate)} data-testid="copy-email">
                {copied.mail ? <><Check className="w-3 h-3 mr-1 text-emerald-600" />コピー済</> : <><Copy className="w-3 h-3 mr-1" />全文コピー</>}
              </Button>
            </div>
            <textarea
              readOnly value={emailTemplate}
              rows={10}
              className="w-full text-xs font-mono border border-slate-200 rounded-lg p-3 bg-slate-50"
              data-testid="email-template"
            />
            <p className="text-[10px] text-slate-400 mt-1">※ このまま {lead.email} 宛にメール送信してください。</p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="issue-close">閉じる</Button>
          <Button
            className="bg-blue-600 hover:bg-blue-700"
            onClick={() => {
              const subject = encodeURIComponent("【やさまるシフト】トライアル用ログインIDのご案内");
              const body = encodeURIComponent(emailTemplate);
              window.open(`mailto:${lead.email}?subject=${subject}&body=${body}`, "_blank");
            }}
            data-testid="open-mail"
          >
            メールアプリで開く
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CredRow({ label, value, onCopy, copied, bold, mono, disabled }) {
  return (
    <div className="flex items-center gap-2 px-3 py-2 border-b border-slate-100 last:border-0 bg-white">
      <span className="text-[11px] text-slate-500 w-28 shrink-0">{label}</span>
      <code className={`flex-1 min-w-0 text-xs ${mono ? "font-mono" : ""} ${bold ? "font-bold" : ""} ${disabled ? "text-slate-400" : "text-slate-800"} truncate`}>
        {value}
      </code>
      {!disabled && onCopy && (
        <button onClick={onCopy} className="text-[11px] text-blue-600 hover:text-blue-800 inline-flex items-center gap-1 shrink-0" data-testid={`copy-${label}`}>
          {copied ? <><Check className="w-3 h-3 text-emerald-600" />コピー済</> : <><Copy className="w-3 h-3" />コピー</>}
        </button>
      )}
    </div>
  );
}

// ===== 詳細ダイアログ =====
function LeadDetailDialog({ lead, onClose }) {
  if (!lead) return null;
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="lead-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building2 className="w-5 h-5 text-blue-600" />{lead.company}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <DetailItem label="お名前" value={lead.name} />
          <DetailItem label="メール" value={lead.email} />
          <DetailItem label="電話" value={lead.phone} />
          <DetailItem label="店舗数" value={lead.store_count} />
          <DetailItem label="業種" value={lead.industry} />
          <DetailItem label="受付日時" value={fmtDate(lead.created_at)} />
          <DetailItem label="ステータス" value={<StatusBadge value={lead.status} />} />
          {lead.coupon_applied && (
            <DetailItem label="キャンペーン" value={
              <span className="inline-flex items-center gap-1 text-[11px] font-bold text-amber-800 bg-gradient-to-r from-amber-100 to-pink-100 border border-amber-300 px-2 py-0.5 rounded">
                🎟️ {lead.coupon_code} — {lead.coupon_label}
              </span>
            } />
          )}
          {lead.issued_login_id && <DetailItem label="発行済みID" value={<code className="font-mono text-emerald-700">{lead.issued_login_id}</code>} />}
          {lead.message && (
            <div>
              <p className="text-xs font-bold text-slate-700 mb-1">ご質問・ご要望</p>
              <p className="text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded p-3 whitespace-pre-wrap">{lead.message}</p>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>閉じる</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DetailItem({ label, value }) {
  return (
    <div className="flex items-start gap-3">
      <span className="text-xs font-bold text-slate-500 w-20 shrink-0 mt-0.5">{label}</span>
      <span className="flex-1 text-sm text-slate-800">{value || <span className="text-slate-300">—</span>}</span>
    </div>
  );
}
